import { Box, Flex, Stack, Text } from '@chakra-ui/layout';
import useStore from '../stores/store';
import ProductTour from './ProductTour';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    TabIndicator,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    useColorModeValue,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger
} from '@chakra-ui/react';
import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import { Sign } from 'crypto';
import { set } from 'lodash';
import {
    createAnonAccount,
    guestSignUpEmailPassword,
    resetPasswordMsg,
    signInEmailPassword,
    signInGoogle,
    signUpEmailPassword,
    guestSignUpGoogle
} from '../../platformFiles/platformMessages';
import useCustomToast from './ToastProvider';

export const AuthModalMiddle = () => {
    const userStatus = useStore((state) => state.userStatus);
    const userData = useStore((state) => state.userData);

    if (userStatus === 'loading') {
        return null;
    } else if (userStatus === 'signedOut') {
        return <AuthModal />;
    } else if (
        (userStatus === 'signedIn' || userStatus === 'guest') &&
        userData?.tour === true
    ) {
        return <ProductTour />;
    } else return null;
};

const AuthModal = () => {
    const [isLoading, setIsLoaded] = useState(null);
    const onClose = () => {};

    return (
        <Modal onClose={onClose} isOpen={true} isCentered size="sm">
            <ModalOverlay />
            <ModalContent rounded="14px" bg={'modalBg'} boxShadow={'xl'}>
                <ModalBody pt="24px">
                    <Tabs
                        position="relative"
                        variant="unstyled"
                        isFitted
                        _focus={{ outline: 'none' }}
                    >
                        <TabList>
                            <Tab
                                fontSize="16px"
                                fontWeight="600"
                                _focus={{ boxShadow: 'none' }}
                                _active={{ color: '#3591ff' }}
                                transition="0.1s"
                            >
                                New account
                            </Tab>
                            <Tab
                                fontSize="16px"
                                fontWeight="600"
                                _focus={{ boxShadow: 'none' }}
                                _active={{ color: '#3591ff' }}
                                transition="0.1s"
                            >
                                Log in
                            </Tab>
                        </TabList>
                        <TabIndicator
                            mt="-1.5px"
                            height="3px"
                            bg="#5DABFE"
                            borderRadius="2px"
                        />
                        <TabPanels>
                            <TabPanel p="8px 0">
                                <NewAccBody
                                    isLoading={isLoading}
                                    setIsLoaded={setIsLoaded}
                                />
                            </TabPanel>
                            <TabPanel p="8px 0">
                                <LoginBody
                                    isLoading={isLoading}
                                    setIsLoaded={setIsLoaded}
                                />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

const NewAccBody = ({ isLoading, setIsLoaded }) => {
    const showToast = useCustomToast();

    const signUpGuest = async () => {
        setIsLoaded('guest');
        let resp = await createAnonAccount();
        if (resp.msg === 'success') {
            useStore.getState().initApp();
        } else {
            showToast({
                title: 'Something went wrong signing up',
                subtitle: resp.errorMessage,
                type: 'error'
            });
        }
    };

    return (
        <>
            <Stack minH="336px">
                <SignUpBody
                    isLoading={isLoading}
                    setIsLoaded={setIsLoaded}
                    isAlreadyGuest={false}
                />
                <OrLine />
                <Button
                    rounded="8px"
                    fontSize="14px"
                    onClick={() => signUpGuest()}
                    isLoading={isLoading === 'guest' ? true : false}
                    isDisabled={isLoading}
                    w="100%"
                    mb="12px"
                >
                    Continue as guest
                </Button>
            </Stack>
        </>
    );
};

const OrLine = () => {
    const lineColor = useColorModeValue('#e4eaf0', '#394146');

    return (
        <Flex my="4px" alignItems="center">
            <Box height="1px" bg={lineColor} mr="12px" ml="4px" w="50%" />
            <Text fontSize="13px" color={'gray.600'}>
                or
            </Text>
            <Box height="1px" bg={lineColor} ml="12px" mr="4px" w="50%" />
        </Flex>
    );
};

export const SignUpBody = ({ isLoading, setIsLoaded, isAlreadyGuest }) => {
    const [emailForm, setEmailForm] = useState(isAlreadyGuest ? true : false);
    const signUpGoogle = () => {
        setIsLoaded('google');
        if (isAlreadyGuest) {
            guestSignUpGoogle(setIsLoaded);
        } else {
            signInGoogle(setIsLoaded);
        }
        /* if (how === 'email') {
            signUpEmailPassword(email, password);
        } else if (how === 'google') {
            s;
        }
        setIsLoaded(how); */
    };

    return (
        <>
            <Text fontSize="15px" fontWeight="500" mt="8px">
                {isAlreadyGuest
                    ? 'Set up your account with an email and password to enable data syncing and access on other devices.' //'Access and sync your data anywhere with an account. Switch your guest account to a cost-free account by signing up below.'
                    : 'Create a free account to secure your data and access it from the web and mobile apps. You can also continue as a guest and sign up later.'}
            </Text>
            <Text
                fontSize="12px"
                color="gray.400"
                fontWeight="500"
                my="4px"
                mr="12px"
            >
                {isAlreadyGuest ? 'By adding your details' : 'By signing up'}{' '}
                you agree to our{' '}
                <a
                    href="https://www.tabextend.com/privacypolicy"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <u>privacy policy</u>
                </a>{' '}
                and{' '}
                <a
                    href="https://www.tabextend.com/termsofuse"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <u>terms of use</u>
                </a>
            </Text>
            {!isAlreadyGuest && (
                <Button
                    fontSize="14px"
                    isLoading={isLoading === 'google' ? true : false}
                    isDisabled={isLoading}
                    onClick={() => signUpGoogle()}
                    w="100%"
                    rounded="8px"
                >
                    <img
                        height="16px"
                        width="16px"
                        src={'../../assets/images/google_icon.png'}
                        style={{ marginRight: '12px' }}
                    />
                    Sign up with Google
                </Button>
            )}
            {!emailForm ? (
                <Button
                    rounded="8px"
                    fontSize="14px"
                    onClick={() => setEmailForm(true)}
                    isLoading={isLoading === 'email' ? true : false}
                    isDisabled={isLoading}
                    w="100%"
                    color="#FFF"
                    backgroundImage="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                    _hover={{
                        backgroundImage:
                            'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                        boxShadow: '0 8px 16px -8px rgba(49, 144, 255,0.3)'
                    }}
                    boxShadow="0 12px 18x -10px rgba(49, 144, 255,0.35)"
                    transition="all 0.12s cubic-bezier(.374,.019,.035,1.861)"
                >
                    {isAlreadyGuest
                        ? 'Update account'
                        : 'Sign up with Email + Password'}
                    <Icon
                        as={!emailForm ? ChevronDownIcon : ChevronUpIcon}
                        pos="absolute"
                        right="16px"
                    />
                </Button>
            ) : (
                <EmailSignUpForm
                    isLoading={isLoading}
                    setLoading={setIsLoaded}
                    isAlreadyGuest={isAlreadyGuest}
                    // signUp={signUp}
                />
            )}
            {isAlreadyGuest && (
                <>
                    <OrLine />
                    <Button
                        fontSize="14px"
                        isLoading={isLoading === 'google' ? true : false}
                        isDisabled={isLoading}
                        onClick={() => signUpGoogle()}
                        w="100%"
                        rounded="8px"
                    >
                        <img
                            height="16px"
                            width="16px"
                            src={'../../assets/images/google_icon.png'}
                            style={{ marginRight: '12px' }}
                        />
                        Link with Google
                    </Button>
                </>
            )}
        </>
    );
};

const handleResponse = (resp, setLoading) => {
    console.log('sign in google2', resp);

    if (resp?.msg === 'success') {
        // showSuccessToast('Successful login. Welcome back!');
        useStore.getState().initApp();
    } else {
        console.log('set signed out');
        // setInnerLoading(false);
        // showErrorNotification('Login failed', resp.errorMessage);
        setLoading(null);
        useStore.getState().setUserStatus('signedOut');
    }
};

const EmailSignUpForm = ({ isLoading, setLoading, isAlreadyGuest }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState(null);

    const signUpMiddle = (how) => {
        if (!email.includes('@') || !email.includes('.')) {
            setErrors({ type: 'email', text: 'Not a valid email' });
            return;
        }
        if (email === '') {
            setErrors({ type: 'email', text: 'Email cannot be empty' });
            return;
        }
        if (password === '') {
            setErrors({ type: 'password', text: 'Password cannot be empty' });
            return;
        }
        if (password.length < 6) {
            setErrors({
                type: 'password',
                text: 'Password must be at least 6 characters'
            });
            return;
        }
        if (password.length > 128) {
            setErrors({
                type: 'password',
                text: 'Password must be less than 128 characters'
            });
            return;
        }
        if (isAlreadyGuest) {
            guestSignUpEmailPassword(email, password, setLoading, true);
        } else {
            signUpEmailPassword(email, password, setLoading);
        }
        setErrors(null);
        setLoading('email');
    };

    return (
        <>
            <InputElement
                icon={EnvelopeIcon}
                placeholder="Email"
                type="email"
                value={email}
                setValue={setEmail}
                error={errors?.type === 'email' ? errors.text : null}
            />
            <PasswordInput
                value={password}
                setValue={setPassword}
                error={errors?.type === 'password' ? errors.text : null}
            />
            <Button
                rounded="8px"
                fontSize="14px"
                onClick={() => signUpMiddle('email')}
                isLoading={isLoading === 'email' ? true : false}
                isDisabled={isLoading}
                w="100%"
                color="#FFF"
                backgroundImage="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                _hover={{
                    backgroundImage:
                        'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                    boxShadow: '0 8px 16px -8px rgba(49, 144, 255,0.3)'
                }}
                boxShadow="0 12px 18x -10px rgba(49, 144, 255,0.35)"
                transition="all 0.12s cubic-bezier(.374,.019,.035,1.861)"
            >
                {isAlreadyGuest ? 'Update account' : 'Sign up'}
            </Button>
        </>
    );
};

const InputElement = ({ icon, placeholder, type, value, setValue, error }) => {
    const handleChange = (event) => setValue(event.target.value);

    return (
        <>
            <InputGroup>
                <InputLeftElement pointerEvents="none" color="gray.300">
                    <Icon as={icon} />
                </InputLeftElement>
                <Input
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    rounded="8px"
                />
            </InputGroup>
            {error && (
                <Text fontSize="12px" color="red.500" mt="0px">
                    {error}
                </Text>
            )}
        </>
    );
};

const PasswordInput = ({ value, setValue, error }) => {
    const handleChange = (event) => setValue(event.target.value);
    const [show, setShow] = useState(false);
    const handleClick = () => setShow(!show);

    return (
        <>
            <InputGroup>
                <InputLeftElement pointerEvents="none" color="gray.300">
                    <Icon as={LockClosedIcon} />
                </InputLeftElement>
                <Input
                    pr="4.5rem"
                    type={show ? 'text' : 'password'}
                    placeholder="Password"
                    value={value}
                    onChange={handleChange}
                />
                <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={handleClick}>
                        {show ? 'Hide' : 'Show'}
                    </Button>
                </InputRightElement>
            </InputGroup>
            {error && (
                <Text fontSize="12px" color="red.500" mt="0px">
                    {error}
                </Text>
            )}
        </>
    );
};

const LoginBody = ({ isLoading, setIsLoaded }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [resetEmail, setResetEmail] = useState('');
    const [errors, setErrors] = useState(null);
    const [forgotPass, setForgotPass] = useState(false);

    const lineColor = useColorModeValue('#e4eaf0', '#394146');

    const login = (how) => {
        if (how === 'google') {
            signInGoogle(setIsLoaded);
            return;
        }
        if (!email.includes('@') || !email.includes('.')) {
            setErrors({ type: 'email', text: 'Not a valid email' });
            return;
        }
        if (email === '') {
            setErrors({ type: 'email', text: 'Email cannot be empty' });
            return;
        }
        if (password === '') {
            setErrors({ type: 'password', text: 'Password cannot be empty' });
            return;
        }
        if (password.length < 6) {
            setErrors({
                type: 'password',
                text: 'Password must be at least 6 characters'
            });
            return;
        }
        if (password.length > 128) {
            setErrors({
                type: 'password',
                text: 'Password must be less than 128 characters'
            });
            return;
        }
        setErrors(null);
        setIsLoaded(how);
        signInEmailPassword(email, password, setIsLoaded);
    };

    const showToast = useCustomToast();

    const resetPassword = async () => {
        if (!resetEmail.includes('@') || !resetEmail.includes('.')) {
            setErrors({ type: 'resetEmail', text: 'Not a valid email' });
            return;
        }
        if (resetEmail === '') {
            setErrors({ type: 'resetEmail', text: 'Email cannot be empty' });
            return;
        }
        setErrors(null);
        setIsLoaded('reset');
        let resp = await resetPasswordMsg(resetEmail);
        if (resp.msg === 'success') {
            showToast({
                title: 'Password reset email sent',
                subtitle: 'Check your email for a password reset link.',
                type: 'success'
            });
        } else {
            showToast({
                title: 'Password reset failed',
                subtitle: resp.errorMessage,
                type: 'error'
            });
        }
    };

    return (
        <>
            <Stack minH="336px">
                <Text fontSize="15px" fontWeight="500" mt="8px">
                    Already have an account? Sign in here:
                </Text>
                <Button
                    fontSize="14px"
                    isLoading={isLoading === 'google' ? true : false}
                    isDisabled={isLoading}
                    onClick={() => login('google')}
                    w="100%"
                    mt="16px"
                    rounded="8px"
                >
                    <img
                        height="16px"
                        width="16px"
                        src={'../../assets/images/google_icon.png'}
                        style={{ marginRight: '12px' }}
                    />
                    Sign in with Google
                </Button>
                <Flex my="4px" alignItems="center">
                    <Box
                        height="1px"
                        bg={lineColor}
                        mr="12px"
                        ml="4px"
                        w="50%"
                    />
                    <Text fontSize="13px" color={'gray.600'}>
                        or
                    </Text>
                    <Box
                        height="1px"
                        bg={lineColor}
                        ml="12px"
                        mr="4px"
                        w="50%"
                    />
                </Flex>
                <InputElement
                    icon={EnvelopeIcon}
                    placeholder="Email"
                    type="email"
                    value={email}
                    setValue={setEmail}
                    error={errors?.type === 'email' ? errors.text : null}
                />
                <PasswordInput
                    value={password}
                    setValue={setPassword}
                    error={errors?.type === 'password' ? errors.text : null}
                />
                <Button
                    fontSize="14px"
                    onClick={() => login('email')}
                    isLoading={isLoading === 'email' ? true : false}
                    isDisabled={isLoading}
                    w="100%"
                    color="#FFF"
                    backgroundImage="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                    _hover={{
                        backgroundImage:
                            'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                        boxShadow: '0 8px 16px -8px rgba(49, 144, 255,0.3)'
                    }}
                    boxShadow="0 12px 18x -10px rgba(49, 144, 255,0.35)"
                    transition="all 0.12s cubic-bezier(.374,.019,.035,1.861)"
                    rounded="8px"
                >
                    Sign in
                </Button>
                <Popover
                    isOpen={forgotPass}
                    onClose={() => setForgotPass(false)}
                >
                    <PopoverTrigger>
                        <Button
                            bg="transparent"
                            onClick={() => setForgotPass(true)}
                            w="100%"
                            size="sm"
                            fontWeight={500}
                            _hover={{
                                bg: 'transparent',
                                textDecoration: 'underline'
                            }}
                        >
                            Forgot your password?
                        </Button>
                    </PopoverTrigger>
                    {forgotPass && (
                        <Box
                            bg="rgba(0,0,0,0.34)"
                            style={{ backdropFilter: 'blur(2px)' }}
                            pos="fixed"
                            left="0"
                            right="0"
                            top="0"
                            h="100vh"
                            w="100vw"
                            onClick={() => setForgotPass(false)}
                        />
                    )}
                    <PopoverContent bg={'modalBg'} rounded="12px">
                        <PopoverCloseButton
                            opacity={0.6}
                            rounded="8px"
                            mt="2px"
                            size="sm"
                        />
                        <PopoverBody p="28px 16px 12px">
                            <Text
                                fontSize="15px"
                                fontWeight="500"
                                mt="8px"
                                mb="8px"
                            >
                                Enter your email to reset your password:
                            </Text>
                            <Stack>
                                <InputElement
                                    icon={EnvelopeIcon}
                                    placeholder="Email"
                                    type="email"
                                    value={resetEmail}
                                    setValue={setResetEmail}
                                    error={
                                        errors?.type === 'resetEmail'
                                            ? errors.text
                                            : null
                                    }
                                />
                            </Stack>
                            <Button
                                mt="8px"
                                fontSize="14px"
                                onClick={resetPassword}
                                isLoading={isLoading === 'reset' ? true : false}
                                isDisabled={isLoading}
                                w="100%"
                                color="#FFF"
                                backgroundImage="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                                _hover={{
                                    backgroundImage:
                                        'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                                    boxShadow:
                                        '0 8px 16px -8px rgba(49, 144, 255,0.3)'
                                }}
                                boxShadow="0 12px 18x -10px rgba(49, 144, 255,0.35)"
                                transition="all 0.12s cubic-bezier(.374,.019,.035,1.861)"
                                rounded="8px"
                            >
                                Send me a reset password link
                            </Button>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            </Stack>
        </>
    );
};
